import { Link, NavLink, useLocation } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { BRAND, NAV, HERO_SERVICES_DROPDOWN } from "@/lib/site-data";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
  }, []);

  useEffect(() => {
    closeMobile();
    setDropdownOpen(false);
  }, [location.pathname, closeMobile]);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeMobile();
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen, closeMobile]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 transition-[background-color,border-color] duration-300 border-b ${
          scrolled
            ? "bg-[#070807]/90 backdrop-blur-xl border-white/5"
            : "bg-[#070807]/60 backdrop-blur-md border-transparent"
        }`}
      >
        <div className="ep-container ep-header-shell flex items-center justify-between gap-3">
          <Link
            to="/"
            className="flex items-center shrink-0"
            data-testid="header-logo"
            aria-label="Epersonel Ana Sayfa"
          >
            <span className="ep-logo-wrap">
              <img
                src={BRAND.logo}
                alt={BRAND.logoAlt}
                className="ep-logo-img"
                width="500"
                height="500"
              />
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Ana menü">
            {NAV.map((item) => {
              if (item.hasDropdown) {
                return (
                  <div
                    key={item.href}
                    className="relative"
                    ref={dropdownRef}
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    <NavLink
                      to={item.href}
                      data-testid="nav-services"
                      className={({ isActive }) =>
                        `flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                          isActive
                            ? "text-[#00F22E]"
                            : "text-white/85 hover:text-[#00F22E]"
                        }`
                      }
                      aria-expanded={dropdownOpen}
                      aria-haspopup="menu"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {item.label}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          dropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </NavLink>
                    {dropdownOpen && (
                      <div
                        className="absolute top-full left-0 pt-2 min-w-[300px]"
                        role="menu"
                      >
                        <div className="ep-glass p-2 rounded-2xl">
                          {HERO_SERVICES_DROPDOWN.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/hizmetlerimiz/${s.slug}`}
                              data-testid={`nav-service-${s.slug}`}
                              className="block px-4 py-2.5 text-sm text-white/85 hover:text-[#00F22E] hover:bg-white/[0.03] rounded-xl transition-colors"
                              role="menuitem"
                            >
                              {s.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.href === "/"}
                  data-testid={`nav-${item.href.replace(/\//g, "") || "home"}`}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                      isActive
                        ? "text-[#00F22E]"
                        : "text-white/85 hover:text-[#00F22E]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/basvuru"
              data-testid="header-cta-analysis"
              className="btn-primary text-[11px] px-2.5 py-2 sm:text-sm sm:px-5 sm:py-3"
            >
              Ücretsiz Analiz İste
            </Link>
            <button
              type="button"
              aria-label={mobileOpen ? "Menüyü kapat" : "Menüyü aç"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-drawer"
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/10 text-white"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer — rendered as sibling to escape sticky stacking context */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/70 z-[70]"
            onClick={closeMobile}
            aria-hidden="true"
            data-testid="mobile-menu-backdrop"
          />
          <div
            id="mobile-drawer"
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Ana menü"
            data-testid="mobile-menu"
            className="lg:hidden fixed top-0 right-0 h-[100dvh] w-[86%] max-w-[380px] bg-[#070807] z-[80] border-l border-white/[0.06] overflow-y-auto flex flex-col"
            style={{ paddingTop: "env(safe-area-inset-top)" }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.05]">
              <span className="ep-logo-wrap ep-logo-wrap-mobile">
                <img
                  src={BRAND.logo}
                  alt={BRAND.logoAlt}
                  className="ep-logo-img"
                  width="500"
                  height="500"
                />
              </span>
              <button
                type="button"
                onClick={closeMobile}
                aria-label="Menüyü kapat"
                data-testid="mobile-menu-close"
                className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/10 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 px-3 py-4" aria-label="Mobil ana menü">
              {NAV.map((item) => {
                if (item.hasDropdown) {
                  return (
                    <div key={item.href}>
                      <button
                        type="button"
                        onClick={() => setMobileServicesOpen((v) => !v)}
                        aria-expanded={mobileServicesOpen}
                        aria-controls="mobile-services-submenu"
                        data-testid="mobile-nav-services-toggle"
                        className="w-full flex items-center justify-between px-4 py-3 text-base rounded-xl text-white/90 hover:bg-white/[0.03]"
                      >
                        <span>{item.label}</span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${
                            mobileServicesOpen ? "rotate-180 text-[#00F22E]" : ""
                          }`}
                        />
                      </button>
                      {mobileServicesOpen && (
                        <div id="mobile-services-submenu" className="pl-3 mt-1 border-l border-[#00F22E]/25 ml-4">
                          <Link
                            to="/hizmetlerimiz"
                            data-testid="mobile-nav-services-all"
                            onClick={closeMobile}
                            className="block px-4 py-2 text-sm text-[#00F22E] hover:bg-white/[0.03] rounded-lg"
                          >
                            Tüm Hizmetler
                          </Link>
                          {HERO_SERVICES_DROPDOWN.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/hizmetlerimiz/${s.slug}`}
                              data-testid={`mobile-nav-service-${s.slug}`}
                              onClick={closeMobile}
                              className="block px-4 py-2 text-sm text-white/80 hover:text-[#00F22E] hover:bg-white/[0.03] rounded-lg"
                            >
                              {s.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    end={item.href === "/"}
                    data-testid={`mobile-nav-${item.href.replace(/\//g, "") || "home"}`}
                    onClick={closeMobile}
                    className={({ isActive }) =>
                      `block px-4 py-3 text-base rounded-xl transition-colors ${
                        isActive
                          ? "text-[#00F22E] bg-white/[0.04]"
                          : "text-white/90 hover:bg-white/[0.03]"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>

            <div className="px-5 pb-6 pt-4 border-t border-white/[0.05]">
              <Link
                to="/basvuru"
                onClick={closeMobile}
                className="btn-primary w-full"
                data-testid="mobile-cta-analysis"
              >
                Ücretsiz Analiz İste
              </Link>
              <div className="mt-4 text-xs text-white/60 text-center">
                <a href={`tel:+${BRAND.contact.phoneRaw}`} className="hover:text-[#00F22E]">
                  {BRAND.contact.phone}
                </a>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
